/**
 * ProvidersPage — admin CRUD for payment_agents + ticket_providers.
 * Tabbed by role. Each row exposes availability, limits, supported currencies/routes,
 * crypto wallets, bank accounts, operational notes, and active-ops count.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Truck, Plus, Pencil, Trash2, RefreshCw, X, CheckCircle2, AlertCircle, ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import {
  listProviders, createProvider, updateProvider, deleteProvider, listOperations,
} from '@/services/workflowApi';
import { providerStatusLabelEs, availabilityLabelEs } from '@/components/workflow/labels';

const ROLES = [
  { key: 'payment_agent', label: 'Agentes de Pago' },
  { key: 'ticket_provider', label: 'Proveedores de Tickets' },
];

const ProvidersPage = () => {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('payment_agent');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [opCounts, setOpCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listProviders(getAuthHeaders, { role: tab });
      setProviders(r.providers || []);
      // Active-ops count per provider — best-effort, single list call
      const allOps = await listOperations(getAuthHeaders, {
        type: tab === 'payment_agent' ? 'remittance' : 'ticket',
        limit: 500,
      });
      const counts = {};
      (allOps.operations || []).forEach((o) => {
        const id = tab === 'payment_agent' ? o.payment_agent_id : o.ticket_provider_id;
        if (id) counts[id] = (counts[id] || 0) + 1;
      });
      setOpCounts(counts);
    } catch (e) {
      toast.error('No se pudieron cargar los proveedores');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, tab]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor? Si tiene operaciones activas, el backend lo rechazará.')) return;
    try {
      await deleteProvider(getAuthHeaders, id);
      toast.success('Proveedor eliminado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Gestión de Proveedores
          </h1>
          <p className="text-xs text-slate-500">Agentes de Pago · Proveedores de Tickets · {providers.length} activos en esta vista</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="providers-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar</Button>
          <Button size="sm" onClick={() => setEditing({ role: tab })} className="bg-blue-600 hover:bg-blue-700" data-testid="provider-new">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo
          </Button>
        </div>
      </div>

      <div className="flex bg-slate-100 rounded-lg p-1 w-fit">
        {ROLES.map((r) => (
          <button
            key={r.key}
            onClick={() => setTab(r.key)}
            data-testid={`role-tab-${r.key}`}
            className={`px-4 py-1.5 text-xs font-semibold rounded ${tab === r.key ? 'bg-white text-slate-900 shadow' : 'text-slate-500'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-3 text-left">Nombre</th>
                <th className="p-3 text-left">País</th>
                <th className="p-3 text-left">Estado</th>
                <th className="p-3 text-left">Disponibilidad</th>
                <th className="p-3 text-left">Límites</th>
                <th className="p-3 text-left">Monedas</th>
                <th className="p-3 text-left">Rutas</th>
                <th className="p-3 text-left">Wallets</th>
                <th className="p-3 text-left">Cuentas</th>
                <th className="p-3 text-right">Ops activas</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="11" className="p-6 text-center text-slate-400">Cargando…</td></tr>
              ) : providers.length === 0 ? (
                <tr><td colSpan="11" className="p-6 text-center text-slate-400 italic">Sin proveedores en esta categoría</td></tr>
              ) : providers.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`provider-row-${p.id}`}>
                  <td className="p-3 font-semibold text-slate-900">
                    <div>{p.name}</div>
                    <div className="text-[10px] text-slate-500 font-normal">{p.email}</div>
                  </td>
                  <td className="p-3">{p.country || '—'}</td>
                  <td className="p-3">
                    <span className={`px-1.5 py-0.5 rounded ring-1 ring-inset font-bold text-[10px] uppercase ${
                      p.status === 'active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
                      : p.status === 'paused' ? 'bg-amber-50 text-amber-700 ring-amber-300'
                      : 'bg-slate-100 text-slate-600 ring-slate-300'
                    }`}>{providerStatusLabelEs(p.status)}</span>
                  </td>
                  <td className="p-3">{availabilityLabelEs(p.availability)}</td>
                  <td className="p-3 whitespace-nowrap">{p.limits?.max_concurrent ?? '—'} concur. · ${p.limits?.daily_cap_usd ?? 0}/d</td>
                  <td className="p-3">{(p.supported_currencies || []).join(', ') || '—'}</td>
                  <td className="p-3">{(p.supported_routes || []).length} rutas</td>
                  <td className="p-3">{(p.crypto_wallets || []).length}</td>
                  <td className="p-3">{(p.bank_accounts || []).length}</td>
                  <td className="p-3 text-right font-mono font-bold text-slate-700">{opCounts[p.id] || 0}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={async () => {
                        try {
                          const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
                          const r = await axios.post(
                            `${API_URL}/admin/providers/${p.role}/${p.id}/preview-token`,
                            {},
                            { headers: getAuthHeaders() },
                          );
                          const url = `${window.location.origin}/preview/launch?token=${encodeURIComponent(r.data.token)}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch {
                          toast.error('No se pudo abrir la vista previa');
                        }
                      }}
                      className="p-1 text-emerald-600 hover:text-emerald-700"
                      title="Vista previa como este proveedor"
                      data-testid={`provider-preview-${p.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditing(p)} className="p-1 text-slate-500 hover:text-blue-600 ml-1" data-testid={`provider-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(p.id)} className="p-1 text-slate-500 hover:text-rose-600 ml-1" data-testid={`provider-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <ProviderFormModal
          initial={editing}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const ProviderFormModal = ({ initial, getAuthHeaders, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    role: initial.role,
    name: initial.name || '',
    email: initial.email || '',
    phone: initial.phone || '',
    country: initial.country || '',
    status: initial.status || 'active',
    availability: initial.availability || 'available',
    limits: initial.limits || { max_concurrent: 5, daily_cap_usd: 0 },
    supported_currencies: (initial.supported_currencies || []).join(','),
    supported_routes: (initial.supported_routes || []).map((r) => `${r.origin}-${r.destination}`).join(','),
    operational_notes: initial.operational_notes || '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        supported_currencies: form.supported_currencies.split(',').map((s) => s.trim()).filter(Boolean),
        supported_routes: form.supported_routes.split(',').map((s) => s.trim()).filter(Boolean)
          .map((pair) => { const [origin, destination] = pair.split('-').map((x) => x.trim()); return { origin, destination }; })
          .filter((r) => r.origin && r.destination),
      };
      if (isEdit) {
        delete payload.role; delete payload.email;
        await updateProvider(getAuthHeaders, initial.id, payload);
      } else {
        await createProvider(getAuthHeaders, payload);
      }
      toast.success(isEdit ? 'Proveedor actualizado' : 'Proveedor creado');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="provider-modal">
      <Card className="w-full max-w-2xl p-5 bg-white space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-900">
            {isEdit ? 'Editar' : 'Nuevo'} {form.role === 'payment_agent' ? 'Agente de Pago' : 'Proveedor de Tickets'}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="provider-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="provider-name" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={isEdit} data-testid="provider-email" />
          <Input placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="provider-phone" />
          <Input placeholder="País" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="provider-country" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border rounded px-2 py-1.5 text-sm" data-testid="provider-status">
            <option value="active">activo</option><option value="paused">pausado</option><option value="inactive">inactivo</option>
          </select>
          <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="border rounded px-2 py-1.5 text-sm" data-testid="provider-availability">
            <option value="available">disponible</option><option value="busy">ocupado</option><option value="offline">desconectado</option>
          </select>
          <Input placeholder="Operaciones simultáneas (máx.)" type="number" value={form.limits.max_concurrent} onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_concurrent: Number(e.target.value) } })} data-testid="provider-max-concurrent" />
          <Input placeholder="Tope diario USD" type="number" value={form.limits.daily_cap_usd} onChange={(e) => setForm({ ...form, limits: { ...form.limits, daily_cap_usd: Number(e.target.value) } })} data-testid="provider-daily-cap" />
        </div>
        <Input placeholder="Monedas (coma) ej: USD,UYU,EUR" value={form.supported_currencies} onChange={(e) => setForm({ ...form, supported_currencies: e.target.value })} data-testid="provider-currencies" />
        <Input placeholder="Rutas (coma) ej: USA-UY, EU-HAV" value={form.supported_routes} onChange={(e) => setForm({ ...form, supported_routes: e.target.value })} data-testid="provider-routes" />
        <textarea placeholder="Notas operativas" value={form.operational_notes} onChange={(e) => setForm({ ...form, operational_notes: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm min-h-[60px]" data-testid="provider-notes" />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="provider-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="provider-save">{busy ? 'Guardando…' : 'Guardar'}</Button>
        </div>
      </Card>
    </div>
  );
};

export default ProvidersPage;
